import { Component, computed, contentChildren, effect, forwardRef, inject, input, model, signal } from '@angular/core';
import { cn } from './cn';

/**
 *   <hlm-tabs [(value)]="tab">
 *     <hlm-tab value="info" label="Information">...</hlm-tab>
 *     <hlm-tab value="contracts" label="Contracts">...</hlm-tab>
 *   </hlm-tabs>
 */
@Component({
  selector: 'hlm-tabs',
  standalone: true,
  template: `
    <div [class]="_rootClass()">
      <div role="tablist" [class]="_listClass()">
        @for (t of tabs(); track t.value()) {
          <button
            type="button"
            role="tab"
            [attr.aria-selected]="t.value() === value()"
            [attr.data-testid]="t.testId() || null"
            (click)="select(t.value())"
            [class]="_trigClass(t.value() === value())"
          >
            {{ t.label() }}
          </button>
        }
      </div>
      <div class="mt-3 flex min-h-0 flex-1 flex-col">
        <ng-content />
      </div>
    </div>
  `,
})
export class HlmTabsComponent {
  readonly value = model<unknown>(null);
  readonly userClass = input<string>('', { alias: 'class' });

  readonly tabs = contentChildren(forwardRef(() => HlmTabComponent));

  protected _rootClass = () => cn('flex flex-col', this.userClass());
  protected _listClass = () =>
    cn('inline-flex items-center gap-1 border-b border-border');
  protected _trigClass = (active: boolean) =>
    cn(
      'relative px-3 pb-2 pt-1.5 text-sm font-medium transition-colors',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 rounded-t',
      active
        ? 'text-foreground after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-[2px] after:bg-primary after:content-[""]'
        : 'text-muted-foreground hover:text-foreground',
    );

  select(v: unknown): void {
    this.value.set(v);
  }

  isActive(v: unknown): boolean {
    return this.value() === v;
  }
}

@Component({
  selector: 'hlm-tab',
  standalone: true,
  template: `
    @if (_parent.isActive(value())) {
      <ng-content />
    }
  `,
  host: {
    class: 'flex min-h-0 flex-1 flex-col',
    '[class.hidden]': '!_parent.isActive(value())',
  },
})
export class HlmTabComponent {
  readonly value = input.required<unknown>();
  readonly label = input.required<string>();
  /**
   * `data-testid` for the trigger button — it is rendered by the parent from a
   * contentChildren query, so a consumer cannot set one from outside.
   */
  readonly testId = input<string>('');
  _parent = inject(HlmTabsComponent);
}

export const HLM_TABS = [HlmTabsComponent, HlmTabComponent] as const;
